import type { DynamicPricing, DynamicSeason } from '../schemas/dynamic-pricing';

// XT-951 — the rules a dynamic pricing structure must keep that its schema
// cannot express: a period ends on or after the day it starts, a
// stay-X-pay-Y promotion frees at least one night, and a net rate's markup
// leaves a price above nothing (over −100 %). The product editor lists a
// broken one under "Fix before saving" and the server refuses to save the
// structure with Dynamic pricing on while one is broken, both from this list
// and in the same words.

export type DynamicPricingRuleIssue = {
  /** Where, in the editor's words: `Season "Low"`, `Promotion 2`, `General`. */
  where: string;
  problem: string;
};

const named = (name: string | null | undefined, fallback: string): string =>
  name?.trim() ? `"${name.trim()}"` : fallback;

/**
 * A season as the consultant reads it: its name, quoted — with its dates when
 * another season has the same name, and its position when the dates match
 * too — or its position when it has no name.
 */
export const seasonLabel = (
  seasons: readonly Pick<DynamicSeason, 'id' | 'name' | 'from' | 'to'>[],
  season: Pick<DynamicSeason, 'id' | 'name' | 'from' | 'to'>
): string => {
  const position = `${seasons.findIndex((s) => s.id === season.id) + 1}`;
  const name = season.name?.trim();
  if (!name) return position;
  const twins = seasons.filter((s) => s.name?.trim() === name);
  if (twins.length <= 1) return `"${name}"`;
  const sameDates = twins.filter(
    (s) => s.from === season.from && s.to === season.to
  );
  return sameDates.length <= 1 && season.from && season.to
    ? `"${name}" (${season.from} – ${season.to})`
    : `"${name}" (${position})`;
};

const endsBefore = (from?: string | null, to?: string | null): boolean =>
  Boolean(from && to && to < from);

export const dynamicPricingRuleIssues = (
  structure: Pick<
    DynamicPricing,
    | 'contractFrom'
    | 'contractTo'
    | 'seasons'
    | 'surcharges'
    | 'promotions'
    | 'priceType'
    | 'rows'
  >
): DynamicPricingRuleIssue[] => {
  const issues: DynamicPricingRuleIssue[] = [];
  // A gross rate's margin is the commission: its markup is not used.
  if (structure.priceType === 'net') {
    structure.rows.forEach((row) => {
      if (row.markup == null || row.markup > -100) return;
      const season = structure.seasons.find((s) => s.id === row.seasonId);
      const position =
        structure.rows.filter((r) => r.seasonId === row.seasonId).indexOf(row) +
        1;
      issues.push({
        where: `Price row ${position} in ${season ? seasonLabel(structure.seasons, season) : 'its season'}`,
        problem:
          'the markup must be above −100 % (the price would be negative).',
      });
    });
  }
  structure.seasons.forEach((season) => {
    if (endsBefore(season.from, season.to)) {
      issues.push({
        where: `Season ${seasonLabel(structure.seasons, season)}`,
        problem: 'the end date is before the start date.',
      });
    }
  });
  if (endsBefore(structure.contractFrom, structure.contractTo)) {
    issues.push({
      where: 'General',
      problem: 'the contract ends before it starts.',
    });
  }
  structure.surcharges.forEach((surcharge, i) => {
    if (endsBefore(surcharge.from, surcharge.to)) {
      issues.push({
        where: `Surcharge ${named(surcharge.name, `${i + 1}`)}`,
        problem: 'the end date is before the start date.',
      });
    }
  });
  structure.promotions.forEach((promotion, i) => {
    if (endsBefore(promotion.stayFrom, promotion.stayTo)) {
      issues.push({
        where: `Promotion ${i + 1}`,
        problem: '"stay to" is before "stay from".',
      });
    }
    if (
      promotion.type === 'stay_pay' &&
      promotion.stayNights != null &&
      promotion.payNights != null &&
      promotion.payNights >= promotion.stayNights
    ) {
      issues.push({
        where: `Promotion ${i + 1}`,
        problem: 'pay (Y nights) must be less than stay (X nights).',
      });
    }
  });
  return issues;
};
